/**
 * The spine of a GROUPED fan — several tails from one root rigged as one
 * chain (the kitsune, Allen 2026-09-25: "it looks like her tails would group
 * fine"). A stopgap until multi-chain rigging: the bundle moves as one mass.
 *
 * `medialPath` seats each station on the centroid of the flesh CONNECTED to
 * it — right for a finger (a neighbour must not pull the centre), wrong here:
 * walked up one tail of three, it would see one tail. The bundle's centre is
 * found by MASS instead: every zone voxel past the root is projected onto the
 * root → mean-tip axis and sliced; each slice's centroid is the bundle's
 * centre at that depth, touching tails or not. The result is a `MedialPath`,
 * so the station placer, the prior and the radii read it unchanged.
 *
 * Voxel-index space. Kernel-pure. Depends on: hands/medial-path (the type),
 * hands/voxel-grid. Depended on by: place-tail.
 */
import type { MedialPath, MedialStation } from '../hands/medial-path.js';
import { voxelCoords, type VolumeGrid, type VoxelPoint } from '../hands/voxel-grid.js';

/** Slice thickness along the axis, voxels. */
const SLICE = 1;

/** The mass spine of a bundle of tails: root → the mean of `tips`. */
export function bundlePath(grid: VolumeGrid, root: VoxelPoint, tips: readonly VoxelPoint[], beyondRoot: (voxel: number) => boolean = () => true): MedialPath {
  const dims = grid.dims as readonly [number, number, number];
  const mean = { x: 0, y: 0, z: 0 };
  for (const t of tips) {
    mean.x += t.x / tips.length;
    mean.y += t.y / tips.length;
    mean.z += t.z / tips.length;
  }
  const axis = { x: mean.x - root.x, y: mean.y - root.y, z: mean.z - root.z };
  const len = Math.hypot(axis.x, axis.y, axis.z);
  if (!(len > 0)) throw new Error('bundlePath: the tips sit on the root');
  const d = { x: axis.x / len, y: axis.y / len, z: axis.z / len };
  // The slices run to the FARTHEST tip's depth, so no tail is cut short.
  const reach = Math.max(...tips.map((t) => (t.x - root.x) * d.x + (t.y - root.y) * d.y + (t.z - root.z) * d.z));
  const n = Math.max(1, Math.ceil(reach / SLICE));
  const sum = Array.from({ length: n }, () => ({ x: 0, y: 0, z: 0, c: 0 }));
  const members: { s: number; p: VoxelPoint }[] = [];
  for (const v of grid.occupied) {
    if (!beyondRoot(v)) continue;
    const [x, y, z] = voxelCoords(v, dims);
    const t = (x - root.x) * d.x + (y - root.y) * d.y + (z - root.z) * d.z;
    if (t <= 0 || t > reach) continue;
    const s = Math.min(n - 1, Math.floor(t / SLICE));
    const a = sum[s]!;
    a.x += x;
    a.y += y;
    a.z += z;
    a.c++;
    members.push({ s, p: { x, y, z } });
  }
  const centre = sum.map((a) => (a.c > 0 ? { x: a.x / a.c, y: a.y / a.c, z: a.z / a.c } : null));
  // Radius: the mean distance of a slice's voxels from its centre, off the axis.
  const rad = new Float64Array(n);
  for (const { s, p } of members) {
    const c = centre[s]!;
    const q = { x: p.x - c.x, y: p.y - c.y, z: p.z - c.z };
    const along = q.x * d.x + q.y * d.y + q.z * d.z;
    rad[s] = rad[s]! + Math.hypot(q.x - along * d.x, q.y - along * d.y, q.z - along * d.z);
  }
  const points: { at: VoxelPoint; radius: number }[] = [];
  sum.forEach((a, s) => {
    if (a.c > 0) points.push({ at: centre[s]!, radius: rad[s]! / a.c });
  });
  const seated = [{ at: root, radius: points[0]?.radius ?? 0 }, ...points];
  const stations: MedialStation[] = [];
  let arc = 0;
  seated.forEach((p, i) => {
    if (i > 0) {
      const q = seated[i - 1]!.at;
      arc += Math.hypot(p.at.x - q.x, p.at.y - q.y, p.at.z - q.z);
    }
    stations.push({ at: p.at, arclength: arc, radius: p.radius });
  });
  const total = arc;
  return {
    stations,
    length: total,
    at(fraction: number): VoxelPoint {
      const target = Math.min(1, Math.max(0, fraction)) * total;
      for (let i = 1; i < stations.length; i++) {
        const a = stations[i - 1]!;
        const b = stations[i]!;
        if (b.arclength >= target) {
          const span = b.arclength - a.arclength;
          const k = span > 0 ? (target - a.arclength) / span : 0;
          return { x: a.at.x + (b.at.x - a.at.x) * k, y: a.at.y + (b.at.y - a.at.y) * k, z: a.at.z + (b.at.z - a.at.z) * k };
        }
      }
      return stations[stations.length - 1]!.at;
    },
  };
}
